/* eslint-disable bsky-internal/avoid-unwrapped-text */
import './primitives.css'

import {type ReactNode} from 'react'
import {Path, Svg} from 'react-native-svg'

import {ROUTES} from '#/lib/routes/constants.ts'
import {useIsMobileWeb} from '#/alf'
import {Button} from '#/components/Button'
import {Loader} from '#/components/Loader'
import {QrCodeViewer} from '#/components/QrCodeViewer/QrCodeViewer'
import {AccentLink, BodyText, SmallText} from '#/wsocial/components/primitives'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import logoSvg from '../../../assets/icon-wsocial.svg'

export function Divider() {
  const tokens = useSemanticTokens()
  return (
    <div
      style={{
        height: 1,
        backgroundColor: tokens.onboarding.borderLow,
        width: '100%',
      }}
    />
  )
}

export function SupportFooter() {
  const tokens = useSemanticTokens()
  const isMobileWeb = useIsMobileWeb()
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        padding: '16px 16px',
      }}>
      <Divider />
      <SmallText muted style={{textAlign: isMobileWeb ? 'center' : 'left'}}>
        Having trouble?{' '}
        <AccentLink
          style={{color: tokens.onboarding.textMedium}}
          href="mailto:support@wsocial.eu"
          data-testid="supportLink">
          Contact support
        </AccentLink>
      </SmallText>
    </div>
  )
}

export function AlreadyInstallWidentityFooter({
  onClick,
}: {
  onClick: () => void
}) {
  const tokens = useSemanticTokens()
  return (
    <span
      style={{
        fontSize: 13,
        fontWeight: 400,
        lineHeight: '17px',
        color: tokens.onboarding.textLow,
        margin: 0,
      }}>
      Have you already set up W Identity?{' '}
      <button
        onClick={onClick}
        data-testid="continueLink"
        style={{
          background: 'none',
          border: 'none',
          color: tokens.onboarding.btnPrimaryBg,
          cursor: 'pointer',
          padding: 0,
          fontSize: 13,
          fontWeight: 400,
          lineHeight: '17px',
        }}>
        Continue
      </button>
      .
    </span>
  )
}

export function TwoLinksFooter({onClick}: {onClick: () => void}) {
  const tokens = useSemanticTokens()
  return (
    <p>
      <SmallText style={{display: 'inline'}} muted>
        Having trouble?{' '}
        <AccentLink
          style={{color: tokens.onboarding.textMedium}}
          href="mailto:support@wsocial.eu"
          data-testid="supportLink">
          Contact support
        </AccentLink>
        .
      </SmallText>{' '}
      <AlreadyInstallWidentityFooter onClick={onClick} />
    </p>
  )
}

export function ErrorMsg({msg: message}: {msg: string}) {
  const tokens = useSemanticTokens()
  if (!message) return null
  return (
    <p
      style={{
        color: tokens.onboarding.negative,
        fontSize: 13,
        lineHeight: '17px',
        margin: 0,
        padding: '8px 0',
      }}>
      {message}
    </p>
  )
}

export function PageShell({children}: {children: ReactNode}) {
  const tokens = useSemanticTokens()
  return (
    <div
      className="SignUpPageShell"
      style={{
        backgroundColor: tokens.onboarding.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        position: 'relative',
      }}>
      <div
        className="SignUpPageShellInner"
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100%',
        }}>
        {children}
      </div>
    </div>
  )
}

export function LogoBlock() {
  return (
    <div
      className="SignUpLogoBlock"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <img src={logoSvg} alt="W Social" style={{width: 80, height: 80}} />
    </div>
  )
}

export function Checkbox({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label: ReactNode
}) {
  const tokens = useSemanticTokens()
  return (
    <div
      style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'}}
      onClick={() => onChange(!checked)}>
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: 6,
          backgroundColor: checked
            ? tokens.onboarding.btnPrimaryBg
            : tokens.onboarding.checkboxBg,
          border: `1px solid ${tokens.onboarding.borderLow}`,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        {checked && (
          <Svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <Path
              stroke={tokens.onboarding.btnPrimaryText}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 12l5 5L20 7"
            />
          </Svg>
        )}
      </div>
      <SmallText style={{fontWeight: 500}}>{label}</SmallText>
    </div>
  )
}

export function NumberedStep({n, text}: {n: number; text: ReactNode}) {
  const tokens = useSemanticTokens()
  return (
    <div style={{display: 'flex', gap: 12, alignItems: 'flex-start'}}>
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: tokens.onboarding.btnPrimaryBg,
          color: tokens.onboarding.bg,
          fontSize: 13,
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
        {n}
      </div>
      <BodyText>{text}</BodyText>
    </div>
  )
}

export function BackButton({
  onPress,
  testID = 'backBtn',
}: {
  onPress: () => void
  testID?: string
}) {
  const tokens = useSemanticTokens()
  return (
    <Button
      label="Go back"
      color="secondary"
      size="large"
      shape="round"
      testID={testID}
      onPress={onPress}>
      <Svg width="18" height="18" viewBox="0 0 24 24" fill="none">
        <Path
          fill={tokens.onboarding.textMedium}
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15.707 4.293a1 1 0 0 1 0 1.414L9.414 12l6.293 6.293a1 1 0 0 1-1.414 1.414l-7-7a1 1 0 0 1 0-1.414l7-7a1 1 0 0 1 1.414 0Z"
        />
      </Svg>
    </Button>
  )
}

export function PrivacyPolicyLink() {
  const tokens = useSemanticTokens()
  return (
    <SmallText muted>
      Read our{' '}
      <AccentLink
        style={{color: tokens.onboarding.textMedium}}
        href={ROUTES.PRIVACY_POLICY}>
        Privacy policy
      </AccentLink>
    </SmallText>
  )
}

export function StoreLinks() {
  return (
    <>
      <AccentLink
        href="https://apps.apple.com/se/app/w-identity/id6758667517"
        target="_blank"
        rel="noopener noreferrer">
        Apple App Store
      </AccentLink>{' '}
      or{' '}
      <AccentLink
        href="https://play.google.com/store/apps/details?id=com.widentity.app"
        target="_blank"
        rel="noopener noreferrer">
        Google Play
      </AccentLink>
    </>
  )
}

export function NumberedList({items}: {items: ReactNode[]}) {
  return (
    <ol
      style={{
        paddingLeft: 22,
        margin: 0,
        textAlign: 'left',
      }}>
      {items.map((item, i) => (
        <li key={i}>
          <BodyText style={{display: 'inline'}}>{item}</BodyText>
        </li>
      ))}
    </ol>
  )
}

export function QrCodeBlock({
  label,
  qrCodeUrl: _qrCodeUrl,
  qrCodeValue,
  logo,
  logoSize,
  statusText,
  statusVariant = 'default',
  size = 253,
}: {
  label?: ReactNode
  qrCodeUrl?: string // ponytail: retained for API compat; currently unused by render logic
  /** Decoded QR text from useQrSession — drives QrCodeViewer; falls back to img when null */
  qrCodeValue?: string | null
  logo?: string
  logoSize?: number
  statusText?: ReactNode
  statusVariant?: 'default' | 'error'
  size?: number
}) {
  const tokens = useSemanticTokens()
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 16,
        flex: 1,
        minWidth: 0,
      }}>
      {label && (
        <p
          style={{
            fontSize: 19,
            fontWeight: 700,
            lineHeight: '25px',
            color: tokens.onboarding.textPrimary,
            margin: 0,
            textAlign: 'center',
            width: '100%',
          }}>
          {label}
        </p>
      )}
      {qrCodeValue ? (
        <QrCodeViewer
          value={qrCodeValue}
          size={size}
          logo={logo ? logo : logoSvg}
          logoSize={logoSize ?? 100}
        />
      ) : (
        <div
          style={{
            width: size,
            height: size,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Loader size="xl" />
        </div>
      )}
      {statusText && (
        <SmallText
          muted={statusVariant !== 'error'}
          style={{
            paddingTop: 16,
            textAlign: 'center',
            color:
              statusVariant === 'error'
                ? tokens.onboarding.negative
                : undefined,
          }}>
          {statusText}
        </SmallText>
      )}
    </div>
  )
}

export function MobileTopNav({onBack}: {onBack: () => void}) {
  const tokens = useSemanticTokens()
  return (
    <div style={{position: 'absolute', top: '75px', left: 0, padding: '8px'}}>
      <button
        className="SignUpMobileBackBtn"
        onClick={onBack}
        aria-label="Go back"
        style={{margin: '8.5px'}}>
        <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <Path
            fill={tokens.onboarding.textPrimary}
            fillRule="evenodd"
            clipRule="evenodd"
            d="M2 8.00003C2.00004 7.82323 2.0703 7.65369 2.19533 7.52869L6.19533 3.52869C6.32107 3.40726 6.48947 3.34006 6.66427 3.34158C6.83907 3.3431 7.00627 3.41321 7.12988 3.53682C7.25348 3.66042 7.3236 3.82763 7.32512 4.00243C7.32663 4.17722 7.25944 4.34563 7.138 4.47136L4.276 7.33336H13.3333C13.5101 7.33336 13.6797 7.4036 13.8047 7.52862C13.9298 7.65365 14 7.82322 14 8.00003C14 8.17684 13.9298 8.34641 13.8047 8.47143C13.6797 8.59646 13.5101 8.66669 13.3333 8.66669H4.276L7.138 11.5287C7.25944 11.6544 7.32663 11.8228 7.32512 11.9976C7.3236 12.1724 7.25348 12.3396 7.12988 12.4632C7.00627 12.5868 6.83907 12.657 6.66427 12.6585C6.48947 12.66 6.32107 12.5928 6.19533 12.4714L2.19533 8.47136C2.0703 8.34637 2.00004 8.17682 2 8.00003Z"
          />
        </Svg>
      </button>
    </div>
  )
}

export function IllustrationArea({children}: {children: ReactNode}) {
  return (
    <div
      className="SignUpIllustrationArea"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      {children}
    </div>
  )
}
